import { useEffect, useRef } from 'react';
import { Video } from '@loomhq/createhq/dist/video-player/context';
import {
  usePlayerFromContext,
  AnalyticsEvents,
  SystemEvents,
  UiEvents,
} from '@loomhq/createhq';
import markAsViewed from './mark-as-viewed';
import createVideoSessionData, { VideoSessionData } from './video-session';
import getVideoIntervalSync from './sync-intervals';
import createSegmentSyncer from './sync-segments';
import addWindowUnloadEvent from './window-unload';
import trackVideoWatchDuration from './total-duration';
import trackCtaEvent from './cta-event';
import { useSelector } from 'react-redux';
import usePlayerEventsListener from './player-events';
import { Segment } from '@loomhq/createhq/dist/video-player/api/insights';

export function useInitVideoSession(video: Video): void {
  const sessionData = useRef<VideoSessionData>();

  useFirstViewListener(video, sessionData.current);
  useIntervalListener(video);
  useSegmentsListener(video, sessionData.current);
  useTotalDurationListener(video);
  useCtaClickListener(video, sessionData.current);
  usePlayerEventsListener(video);

  useEffect(() => {
    sessionData.current = createVideoSessionData({
      videoId: video.modelId || '',
      trimId: video.processingInformation?.trimId,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function useSegmentsListener(video: Video, sessionData?: VideoSessionData) {
  const player = usePlayerFromContext();

  useEffect(() => {
    if (video.isOwner || !player) {
      return;
    }

    const syncSegments = createSegmentSyncer();

    const onSegment = (segment: Segment) => {
      syncSegments({
        segment,
        videoId: video.modelId || '',
        speed: player.playbackRate,
        sessionId: sessionData?.id,
      });
    };

    player.on([AnalyticsEvents.segment], onSegment);

    return () => {
      player.off([AnalyticsEvents.segment], onSegment);
    };
  }, [player, video, sessionData]);
}

function useIntervalListener(video: Video) {
  const player = usePlayerFromContext();

  useEffect(() => {
    if (video.isOwner || !player) {
      return;
    }

    const syncIntervals = getVideoIntervalSync();
    const videoId = video.modelId || '';

    const onInterval = (buckets: boolean[]) => {
      syncIntervals({ videoId, buckets });
    };

    const onInitialLoad = () => {
      const initialBuckets = player.intervals;

      syncIntervals({ videoId, buckets: initialBuckets });
    };

    player.on([SystemEvents.ready], onInitialLoad);
    player.on([AnalyticsEvents.interval], onInterval);

    return () => {
      player.off([SystemEvents.ready], onInitialLoad);
      player.off([AnalyticsEvents.interval], onInterval);
    };
  }, [player, video.isOwner, video.modelId]);
}

function useFirstViewListener(
  video: Video,
  sessionData?: VideoSessionData
): void {
  const player = usePlayerFromContext();

  useEffect(() => {
    if (video.isOwner || !player) {
      return;
    }

    async function onView() {
      try {
        await markAsViewed(sessionData);
      } catch (e) {
        console.log('unable to mark first view');
      }
    }

    player.on([AnalyticsEvents.view], onView);

    return () => {
      player.off([AnalyticsEvents.view], onView);
    };
  }, [player, sessionData, video.isOwner]);
}

function useTotalDurationListener(video: Video) {
  const player = usePlayerFromContext();
  // This hurts my feelings but we need to reach into redux to get the signed in users workspace
  const selectedWorkspaceId = useSelector(
    (state: any) => state.workspace.selectedWorkspace?.id
  );

  useEffect(() => {
    if (video.isOwner || !player) {
      return;
    }

    const onUnload = async () => {
      const totalDuration = player.totalTimePlayed;

      try {
        if (totalDuration > 0) {
          await trackVideoWatchDuration({
            videoId: video.modelId || '',
            player,
            totalDuration,
            selectedWorkspaceId,
          });
        }
      } catch (e) {
        console.log('unable to track the total duration');
      }
    };

    const removeListener = addWindowUnloadEvent(onUnload);

    return () => {
      removeListener();
    };
  }, [player, video.isOwner, video.modelId, selectedWorkspaceId]);
}

function useCtaClickListener(video: Video, sessionData?: VideoSessionData) {
  const player = usePlayerFromContext();

  useEffect(() => {
    if (video.isOwner || !player || !sessionData) {
      return;
    }

    const onCtaClick = () => {
      trackCtaEvent({
        videoId: video.modelId || '',
        sessionId: sessionData.id,
        isOwner: Boolean(video.isOwner),
      });
    };

    player.on([UiEvents.ctaClicked], onCtaClick);

    return () => {
      player.off([UiEvents.ctaClicked], onCtaClick);
    };
  }, [player, video, sessionData]);
}
